/**
 * Общие словари подписей ОСНОВА.Сметчик: важность находок, решения HITL,
 * форматирование чисел и словари контура «Пакет акта».
 *
 * # Зачем отдельный модуль
 *
 * Эти функции жили КОПИЯМИ в каждой странице сметчика (`KsChecks`, `GsChecks`,
 * `VolumeChecks`, `WriteOffChecks`, `TenderEstimates`, `ZviChecks`,
 * `KsPackages`). Копии — не косметическая проблема: сметчик читает «принято /
 * отклонено / спорно» в шести разных таблицах одного агента, и разъехавшийся
 * перевод одного и того же решения означает, что две страницы утверждают про
 * одну сущность разное. Ровно так же расходились разряды в деньгах: где-то
 * копейки печатались, где-то нет.
 *
 * # Чего здесь НЕТ намеренно
 *
 * Словарей СТАТУСОВ конкретных проверок (`analyzing` / `parsed` / `calculating`
 * и т. п.). Они выглядят похоже, но описывают РАЗНЫЕ жизненные циклы: у Функции
 * 1 это очередь разбора, у Функции 6 — расчёт сверки, у пакета акта — сборка и
 * выпуск. Свести их в один словарь значило бы завести место, где статус одной
 * сущности молча получает подпись от другой. Статусы пакета акта — исключение и
 * лежат здесь ([`packageStatusLabel`]) только потому, что их читает и страница
 * пакетов, и её будущие соседи по контуру (реестр форм, карточка договора).
 */

/** Тон бейджа — совпадает с `$tone` у `shared/ui/Badge`. */
export type SmetaTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

// ─────────────────────────── находки и решения ───────────────────────────

/** Важность находки: error | warning | info. */
export function severityTone(severity: string): SmetaTone {
  switch (severity) {
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Решение сметчика по находке (HITL).
 *
 * `pending` возвращается КАК ЕСТЬ, а не как «—»: страницы показывают этот
 * ярлык только рядом с уже принятым решением, а неразобранная находка держит
 * выпуск пакета — и подменять её прочерком значило бы прятать причину блокировки.
 */
export function decisionLabel(decision: string): string {
  switch (decision) {
    case 'accepted':
      return 'принято';
    case 'rejected':
      return 'отклонено';
    case 'disputed':
      return 'спорно';
    case 'pending':
      return 'не разобрано';
    default:
      return decision;
  }
}

/** Тон решения: спорное — предупреждение, отклонённое — нейтральное. */
export function decisionTone(decision: string): SmetaTone {
  switch (decision) {
    case 'accepted':
      return 'success';
    case 'disputed':
      return 'warning';
    case 'rejected':
      return 'neutral';
    default:
      return 'info';
  }
}

// ─────────────────────────────── числа ───────────────────────────────────

/**
 * Количество: до четырёх знаков. ЕДИНСТВЕННЫЙ формат количеств во всём разделе
 * сметчика — накопления, реестры, тендерные расчёты, списания, ЗВИ, объёмы, КС.
 *
 * Четыре, а не три: в актах встречаются объёмы вида 0,0058 т, и три знака
 * превратили бы такую строку в 0,006 — то есть в другое число, по которому
 * сметчик потом не найдёт расхождение с бланком. Ровно так и печатали местные
 * копии этой функции: одно и то же количество на соседних страницах одного
 * агента округлялось по-разному. Разряды здесь — МАКСИМУМ, а не фиксированная
 * ширина: целое остаётся целым, лишних нулей не появляется.
 *
 * Только КОЛИЧЕСТВА. Деньги печатает [`fmtMoney`], а величину, у которой единица
 * измерения известна лишь в рантайме (полосы тендерных бенчмарков), —
 * [`fmtBenchmarkValue`]: четыре знака у рублей означают, что одна и та же сумма
 * на соседних строках округлена по-разному.
 */
export function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
}

/** Деньги: всегда с копейками — «1 200» и «1 200,00» в акте читаются по-разному. */
export function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Процент: один знак и знак «%» — «12,3%».
 *
 * ЕДИНСТВЕННЫЙ формат процентов раздела. Жил копией в `TenderEstimates`, из-за
 * чего одна и та же величина (НР 92%) печаталась на странице дважды по-разному:
 * в профиле расчёта — «92%», в эталонной полосе — «91,6667» без знака процента.
 * Сметчик читал это как две разные величины и сравнивал их между собой.
 */
export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

/**
 * Денежная ли метрика эталонной полосы (`TenderBenchmark.metric`).
 *
 * Метрика приезжает СТРОКОЙ, и в одном списке лежат доли (`ozp_share`,
 * `mat_share`), проценты (`nr_pct`, `sp_pct`) и рубли (`rub_per_unit`).
 * Проверяем не известное значение, а ФОРМУ имени: backend заводит новые метрики
 * раньше фронта, и незнакомая рублёвая полоса иначе молча напечаталась бы
 * количественным форматом.
 */
function isMoneyMetric(metric: string): boolean {
  return metric.startsWith('rub_') || metric.endsWith('_rub');
}

/** Процентная ли метрика полосы: `nr_pct`, `sp_pct` и будущие соседи. */
function isPctMetric(metric: string): boolean {
  return metric.endsWith('_pct');
}

/**
 * Значение эталонной полосы: рубли — деньгами, проценты — процентами, доли —
 * количеством.
 *
 * Одним `fmtNum` печатать нельзя: в одной таблице стоят и доли, и проценты, и ₽
 * на единицу мощности. Четыре знака у рублёвой полосы («48 123,4568») — не
 * точность, а мусор: рядом та же величина из НЦС печатается `fmtMoney` с
 * копейками, и сметчик сравнивает два по-разному округлённых числа. Ровно то же
 * с процентами: «91,6667» против «92%» в профиле того же расчёта.
 *
 * ЕДИНИЦУ здесь НЕ дописываем. `rub_per_unit` — это рубли НА ЕДИНИЦУ МОЩНОСТИ
 * (backend делит `current_total` на `capacity_value` и хранит рядом
 * `capacity_unit`, потому что смешивать ₽/м² и ₽/кВт нельзя), и суффикс «₽»
 * в ячейке утверждал бы, что перед сметчиком просто сумма. Единица — отдельной
 * колонкой ([`benchmarkUnitLabel`]), как в соседней таблице НЦС.
 */
export function fmtBenchmarkValue(metric: string, v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (isMoneyMetric(metric)) return fmtMoney(v);
  if (isPctMetric(metric)) return fmtPct(v);
  return fmtNum(v);
}

/**
 * Единица эталонной полосы — для отдельной колонки таблицы.
 *
 * У рублёвой полосы знаменатель приезжает строкой (`capacity_unit`: м², кВт,
 * км), и без него величина неинтерпретируема: «48 123,46» это и цена метра, и
 * цена киловатта. Пустой `capacity_unit` у рублёвой метрики честнее показать
 * как «₽ / ед.», чем как просто «₽»: делили всё равно на мощность.
 */
export function benchmarkUnitLabel(
  metric: string,
  capacityUnit: string | null | undefined,
): string {
  if (isMoneyMetric(metric)) return capacityUnit ? `₽ / ${capacityUnit}` : '₽ / ед.';
  if (isPctMetric(metric)) return '%';
  if (metric.endsWith('_share')) return 'доля';
  return '—';
}

// ───────────────────── контур «Пакет акта» (КС-2/КС-3) ─────────────────────

/** Статусы, на которых пакет ещё «в работе»: карточку нужно поллить. */
export const PACKAGE_RUNNING = new Set(['building', 'rendering']);

/** Выпущенный пакет заморожен: правки и пересборка запрещены на backend. */
export const PACKAGE_RELEASED = new Set(['issued', 'archived']);

export function packageStatusTone(status: string): SmetaTone {
  switch (status) {
    case 'built':
    case 'checked':
    case 'issued':
      return 'success';
    case 'building':
    case 'rendering':
      return 'info';
    case 'build_failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function packageStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'черновик';
    case 'building':
      return 'сборка…';
    case 'built':
      return 'собран';
    case 'build_failed':
      return 'ошибка сборки';
    case 'rendering':
      return 'рендер форм…';
    case 'checked':
      return 'проверен';
    case 'issued':
      return 'выпущен';
    case 'archived':
      return 'архив';
    default:
      return status;
  }
}

/** Матч строки на позицию эталона: unique | ambiguous | no_match | manual. */
export function matchTone(status: string): SmetaTone {
  switch (status) {
    case 'unique':
    case 'manual':
      return 'success';
    case 'ambiguous':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function matchLabel(status: string): string {
  switch (status) {
    case 'unique':
      return 'сматчено';
    case 'manual':
      return 'вручную';
    case 'ambiguous':
      return 'неоднозначно';
    case 'no_match':
      return 'нет в смете';
    default:
      return status;
  }
}

/**
 * Вид находки сборки (`ks_package_issues.kind`).
 *
 * Словарь неполный НАМЕРЕННО: неизвестный вид возвращается как есть, а не
 * прячется за «прочее». Backend заводит новые виды раньше фронта, и «прочее»
 * означало бы, что сметчик видит находку, но не понимает, о чём она.
 */
export function issueKindLabel(kind: string): string {
  switch (kind) {
    case 'no_match':
      return 'нет в смете';
    case 'ambiguous_match':
      return 'неоднозначный матч';
    case 'qty_over_contract':
      return 'объём сверх договора';
    case 'price_missing':
      return 'нет цены';
    case 'norm_missing':
      return 'нет нормы расхода';
    case 'unit_mismatch':
      return 'единица измерения';
    case 'zero_total':
      return 'нулевой итог';
    case 'period_overlap':
      return 'пересечение периодов';
    case 'duplicate_period':
      return 'дубль периода';
    case 'duplicate_line':
      return 'дубль строки';
    case 'article_unmapped':
      return 'статья не размечена';
    case 'title_unmapped':
      return 'титул не размечен';
    case 'lsr_unmapped':
      return 'ЛСР не размечена';
    case 'estimate_version_changed':
      return 'версия эталона изменилась';
    case 'estimate_deleted':
      return 'эталон удалён';
    case 'balance_negative':
      return 'отрицательное сальдо';
    case 'compensations_not_ready':
      return 'компенсации не рассчитаны';
    case 'separation_item_missing':
      return 'нет позиции разделительной ведомости';
    case 'signatory_missing':
      return 'нет подписанта';
    case 'render_failed':
      return 'рендер формы';
    // Комплектность материального контура: у акта есть материалы, а бланка под
    // них в профиле договора нет — акт уехал бы заказчику без ведомости.
    case 'doc_missing':
      return 'нет формы в комплекте';
    case 'nomenclature_missing':
      return 'нет номенклатурного номера';
    default:
      return kind;
  }
}

/** Виды первичных документов пакета (§2.17). Порядок — от самых частых. */
export const DOC_KINDS: { value: string; label: string }[] = [
  { value: 'transfer_material', label: 'Акт передачи МТР в монтаж' },
  { value: 'transfer_equipment', label: 'Акт передачи оборудования (ОС-15)' },
  { value: 'delivery_note', label: 'Приходная накладная (М-15 / ТОРГ-12 / ТТН)' },
  { value: 'return_note', label: 'Возвратная накладная' },
  { value: 'invoice', label: 'Счёт-фактура' },
  { value: 'price_protocol', label: 'Протокол согласования цены' },
  { value: 'waybill', label: 'Путевой лист' },
  { value: 'separation_sheet', label: 'Разделительная ведомость' },
];

export function docKindLabel(kind: string): string {
  return DOC_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

/** Печатные формы комплекта, которые уже выпускаются. */
export const FORM_LABELS: Record<string, string> = {
  ks2: 'КС-2',
  ks3: 'КС-3',
  ks6a: 'КС-6а',
  registry_ks2: 'Реестр актов КС-2',
  registry_ks2_b: 'Реестр актов с корзинами затрат',
  vvmp: 'ВВМП (материалы Подрядчика)',
  vvdm: 'ВВДМ (давальческие материалы)',
  vso: 'ВСО (оборудование Заказчика)',
  app1: 'Приложение №1 к КС-2',
  m19_title: 'М-19 Титул',
  m19: 'М-19',
  m29_matrix: 'М-29 (матричный)',
  m29_registry: 'М-29 (регистровый)',
  fot: 'ФОТ (компенсация зарплаты)',
  delivery: 'Доставка (компенсация перевозки)',
};

export function formLabel(code: string): string {
  return FORM_LABELS[code] ?? code;
}

/**
 * Бланки-спутники АКТА — те, что выпускаются по одному на акт.
 *
 * Список общий для обоих миров и не фильтруется на фронте намеренно: какие
 * формы выпускает договор, знает его профиль (`contract_form_specs`), и
 * повторять эту развилку здесь значило бы держать правило в двух местах.
 * Форму, которой у акта нет оснований (нет материалов такой принадлежности, нет
 * калибровки ФОТ, нет рейсов), backend просто не поставит в план — а спрятанная
 * кнопка читалась бы как «такая форма не поддерживается».
 *
 * Порядок — порядок подшивки комплекта: акт, расшифровки материалов, компенсации.
 */
export const ACT_FORM_CODES = [
  'ks2',
  'vvmp',
  'vvdm',
  'app1',
  'm19_title',
  'm19',
  'm29_matrix',
  'm29_registry',
  'vso',
  'fot',
  'delivery',
] as const;

/** Принадлежность материала: своя поставка / поставка Заказчика. */
export function supplyLabel(supply: string): string {
  switch (supply) {
    case 'customer':
      return 'поставка Заказчика';
    case 'contractor':
      return 'поставка Подрядчика';
    default:
      return supply;
  }
}

/** Вид материала: материал или смонтированное оборудование. */
export function materialKindLabel(kind: string): string {
  switch (kind) {
    case 'material':
      return 'материалы';
    case 'equipment':
      return 'оборудование';
    default:
      return kind;
  }
}

/**
 * Куда попадает материал такой принадлежности — подсказка под сводом.
 *
 * Здесь, а не в вёрстке страницы: это правило контура (§4 спецификации), и
 * перепутанная принадлежность означает счёт заказчику за его же трубу.
 */
export function materialDestinationHint(supply: string, kind: string): string {
  if (kind === 'equipment') {
    return supply === 'customer'
      ? 'ВСО, в итог акта не входит (справочно)'
      : 'оборудование Подрядчика — входит в итог акта';
  }
  return supply === 'customer'
    ? 'ВВДМ / М-19 / М-29, в итог акта не входит (справочно)'
    : 'ВВМП (мир A) либо Приложение №1 (мир B), входит в итог акта';
}
